import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Power, RefreshCw, RotateCcw, ShieldAlert } from 'lucide-react';
import { ADMIN_PALETTE } from '../adminDesignSystem';
import { API, formatEur } from './complianceShared';

export function MyDataControlTab({ headers }) {
  const [settings, setSettings] = useState(null);
  const [excluded, setExcluded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, xRes] = await Promise.all([
        axios.get(`${API}/admin/mydata/settings`, { headers }),
        axios.get(`${API}/admin/mydata/excluded-transactions`, { headers }),
      ]);
      setSettings(sRes.data);
      setExcluded(xRes.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load myDATA settings');
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async () => {
    if (!settings) return;
    const next = !settings.enabled;
    const label = next ? 'ENABLE' : 'DISABLE';
    const confirmMsg = next
      ? 'Re-enable myDATA submissions?\n\nAll future paid transactions will be reported to AADE. Already-excluded transactions stay excluded.'
      : 'Disable myDATA submissions?\n\nAll new paid transactions will be flagged as excluded and NEVER reported to AADE — safe for LIVE payment testing.';
    if (!window.confirm(`${label} myDATA — are you sure?\n\n${confirmMsg}`)) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/mydata/settings`, { enabled: next }, { headers });
      setSettings(res.data);
      toast.success(`myDATA submissions ${next ? 'ENABLED' : 'DISABLED'}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };

  const reinclude = async (txnId) => {
    if (!window.confirm('Re-include this transaction in the next monthly myDATA submission?')) return;
    try {
      await axios.post(`${API}/admin/transactions/${txnId}/mydata-exclude`, { exclude: false }, { headers });
      toast.success('Transaction re-included');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to re-include'); }
  };

  if (loading) return <p style={{ color: ADMIN_PALETTE.inkMuted, fontSize: 13 }}>Loading…</p>;
  if (!settings) return null;

  const enabled = !!settings.enabled;

  return (
    <div data-testid="mydata-control-tab" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        data-testid="mydata-status-banner"
        style={{
          padding: 18,
          borderRadius: 14,
          border: `2px solid ${enabled ? '#16A34A' : '#DC2626'}`,
          background: enabled ? '#F0FDF4' : '#FEF2F2',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
        }}
      >
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: enabled ? '#DCFCE7' : '#FEE2E2',
          color: enabled ? '#166534' : '#991B1B',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Power size={22} />
        </div>
        <div style={{ flex: '1 1 260px' }}>
          <div style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: enabled ? '#166534' : '#991B1B' }}>
            myDATA submissions are {enabled ? 'ENABLED' : 'DISABLED'}
          </div>
          <div style={{ fontSize: 12.5, color: ADMIN_PALETTE.inkMuted, marginTop: 4 }}>
            {enabled
              ? 'New paid transactions will be reported to AADE in the next monthly batch.'
              : 'New paid transactions are stamped as excluded and will NEVER be reported. Safe for LIVE payment testing.'}
          </div>
          {settings.updated_at && (
            <div style={{ fontSize: 11, color: ADMIN_PALETTE.inkMuted, marginTop: 6 }}>
              Last changed {new Date(settings.updated_at).toLocaleString()} by <strong>{settings.updated_by_name || settings.updated_by || '—'}</strong>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={toggleEnabled}
          disabled={saving}
          data-testid="mydata-toggle-button"
          style={{
            border: 'none',
            background: enabled ? '#DC2626' : '#16A34A',
            color: '#fff',
            padding: '10px 18px',
            borderRadius: 10,
            fontWeight: 700,
            fontSize: 13,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}
        >
          <Power size={14} /> {saving ? '…' : (enabled ? 'Disable submissions' : 'Enable submissions')}
        </button>
      </div>

      <div style={{ padding: 14, borderRadius: 14, border: `1px solid ${ADMIN_PALETTE.border}`, background: ADMIN_PALETTE.surface }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <ShieldAlert size={16} color={ADMIN_PALETTE.brandRed} />
          <h3 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 17, color: ADMIN_PALETTE.ink }}>
            Excluded transactions ({excluded.length})
          </h3>
          <span style={{ fontSize: 12, color: ADMIN_PALETTE.inkMuted }}>
            — permanently skipped from all future monthly myDATA submissions.
          </span>
          <button type="button" onClick={load} data-testid="mydata-excluded-refresh"
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 8, border: `1px solid ${ADMIN_PALETTE.border}`, background: ADMIN_PALETTE.surface, fontSize: 12, cursor: 'pointer' }}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        {excluded.length === 0 ? (
          <div data-testid="mydata-excluded-empty" style={{ padding: 20, borderRadius: 10, border: `1px dashed ${ADMIN_PALETTE.border}`, textAlign: 'center', color: ADMIN_PALETTE.inkMuted, fontSize: 13 }}>
            No excluded transactions. Any test transaction paid while myDATA is disabled will appear here.
          </div>
        ) : (
          <ul data-testid="mydata-excluded-list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {excluded.map(t => (
              <li key={t.id}
                data-testid={`mydata-excluded-row-${t.id}`}
                style={{ padding: 10, borderRadius: 10, background: ADMIN_PALETTE.surfaceMuted, border: `1px solid ${ADMIN_PALETTE.border}`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 260px' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: ADMIN_PALETTE.ink }}>
                    {t.venue_name || t.venue_id || 'Unknown venue'} · {formatEur(t.total_customer_pays)}
                  </div>
                  <div style={{ fontSize: 11.5, color: ADMIN_PALETTE.inkMuted, marginTop: 3 }}>
                    Paid {t.paid_at ? new Date(t.paid_at).toLocaleString() : '—'} · Fee {formatEur(t.app_fee)} · Txn <code>{(t.id || '').slice(0, 8)}</code>
                  </div>
                  {t.mydata_exclude_reason && (
                    <div style={{ fontSize: 11, color: '#991B1B', marginTop: 3, fontStyle: 'italic' }}>
                      {t.mydata_exclude_reason}
                    </div>
                  )}
                </div>
                <button type="button" onClick={() => reinclude(t.id)}
                  data-testid={`mydata-reinclude-${t.id}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${ADMIN_PALETTE.border}`, background: ADMIN_PALETTE.surface, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  <RotateCcw size={12} /> Re-include
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
